import { useState, useEffect, useMemo } from 'react';
import { auth, useAuthState } from 'api/services/firebase';
import useAccountStore from 'store/accountStore';
import useCompetitionStore from 'store/competitionStore';
import useJoinedCompetitions from 'hooks/useJoinedCompetitions';

const useIsJoined = (competitionId) => {
  const [user] = useAuthState(auth);
  const [isChecking, setIsChecking] = useState(true); // Tracks whether verification is ongoing
  useJoinedCompetitions();

  const joinedCompetitions = useAccountStore(
    (state) => state.accountCompetitions.competitions,
  );

  const isLoading = useAccountStore(
    (state) => state.accountCompetitions.isLoading,
  );

  const competitions = useCompetitionStore(
    (state) => state.competitions,
  );

  // Memoize the computation of `isJoined` to avoid triggering re-renders unnecessarily
  const isJoined = useMemo(() => {
    if (!user || !joinedCompetitions || !joinedCompetitions.length || !competitions || !competitionId) {
      return false;
    }
    const accountCompetitionIds = joinedCompetitions.map((comp) => comp.competitionId);
    return accountCompetitionIds.includes(competitionId);
  }, [user, joinedCompetitions, competitions, competitionId]);

  useEffect(() => {
    if (!user || !joinedCompetitions || !competitions || !competitionId) {
      setIsChecking(false);
      return undefined; // Explicitly return undefined for consistency
    }

    const timeout = setTimeout(() => {
      setIsChecking(false);
    }, 0);

    return () => clearTimeout(timeout); // Always return a cleanup function
  }, [user, joinedCompetitions, competitions, competitionId]);

  return { isJoined, isLoading, isChecking };
};

export default useIsJoined;
